import type { Band, GuessResult, Status } from "./rules.ts";

/** The short line over the globe after a guess, as the app says it: "Closer!", or praise for the answer. */
export interface FeedbackLine { text: string; isPraise: boolean }

/** Nil when there's nothing to say: no guesses yet, or the last guess lost the game. `seed` picks the praise. */
export function feedbackLine(rs: GuessResult[], st: Status, seed: string): FeedbackLine | null {
  const last = rs.at(-1);
  if (!last) return null;
  if (last.band === "correct") return { text: praise(rs.length, seed), isPraise: true };
  if (st !== "playing") return null;
  if (last.band === "nextDoor") return { text: BAND_LINES.nextDoor, isPraise: false };
  const earlier = rs.slice(0, -1).map((r) => r.distanceKm);
  if (earlier.length > 0) {
    const best = Math.min(...earlier);
    if (last.distanceKm < best) return { text: "Closer!", isPraise: false };
    if (last.distanceKm > best) return { text: "Try the other way", isPraise: false };
  }
  return { text: BAND_LINES[last.band], isPraise: false };
}

/** Strong praise only for strong results. Two lines for each count; the photo picks one. */
export function praise(guessCount: number, seed: string): string {
  const second = [...seed].reduce((sum, ch) => sum + ch.codePointAt(0)!, 0) % 2 === 1;
  switch (Math.min(Math.max(guessCount, 1), 6)) {
    case 1: return second ? "Found it first time!" : "First try!";
    case 2: return second ? "Two guesses. Nicely done" : "Found in 2. Sharp eye";
    case 3: return second ? "Found in 3. Nice one" : "Great find";
    case 4: return second ? "Got there in 4" : "Nice work";
    case 5: return second ? "Found it. Well played" : "Got it!";
    default: return second ? "Last guess, and you got it!" : "Found it on your last guess!";
  }
}

/** The line first, then what was already announced, with a pause the line may not have of its own. */
export const withLine = (line: FeedbackLine | null, said: string): string =>
  line ? `${line.text}${/[.!?]$/.test(line.text) ? " " : ". "}${said}` : said;

const BAND_LINES: Record<Band, string> = {
  hot: "Hot! You're close",
  warm: "Warm! Keep going",
  mild: "Getting there. Follow the arrow",
  far: "Keep going. Follow the arrow",
  nextDoor: "So close! It's next door",
  correct: "Correct",
};
